import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { Radio, Trophy, Car, Gauge } from 'lucide-react';

// Parse a display lap-time string ("1:23.456" or "83.456") into seconds.
function lapToSeconds(t) {
  if (!t || typeof t !== 'string') return null;
  const parts = t.trim().split(':');
  if (parts.length === 2) {
    const m = parseInt(parts[0], 10);
    const s = parseFloat(parts[1]);
    return Number.isFinite(m) && Number.isFinite(s) ? m * 60 + s : null;
  }
  const v = parseFloat(parts[0]);
  return Number.isFinite(v) ? v : null;
}

export default function LiveLeaderboard() {
  const [rigs, setRigs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const list = await base44.entities.RigLiveFeed.list('-updated_date', 100);
        if (mounted) { setRigs(list || []); setLoading(false); }
      } catch {
        if (mounted) setLoading(false);
      }
    };
    load();

    const unsubscribe = base44.entities.RigLiveFeed.subscribe((event) => {
      setRigs((prev) => {
        const i = prev.findIndex((r) => r.id === event.id);
        if (event.type === 'delete') return prev.filter((r) => r.id !== event.id);
        if (i >= 0) {
          const next = [...prev];
          next[i] = event.data;
          return next;
        }
        return [...prev, event.data];
      });
    });

    return () => { mounted = false; unsubscribe(); };
  }, []);

  const ranked = rigs
    .filter((r) => r.driver_name && r.best_lap_time)
    .map((r) => ({ ...r, seconds: lapToSeconds(r.best_lap_time) }))
    .filter((r) => r.seconds != null)
    .sort((a, b) => a.seconds - b.seconds)
    .slice(0, 20);

  const liveCount = rigs.filter((r) => r.is_live || r.status === 'live').length;

  return (
    <section className="relative overflow-hidden rounded-lg border border-primary/30 bg-gradient-to-b from-primary/5 to-transparent">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(90% 70% at 0% 0%, rgba(217,35,45,0.10) 0%, rgba(8,9,12,0) 55%)' }}
      />
      <div className="relative px-5 py-6 md:px-7 md:py-7">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
            </span>
            <div>
              <p className="text-primary text-[10px] font-bold tracking-widest leading-none">LIVE NOW</p>
              <h2 className="text-white font-display font-black text-xl md:text-2xl uppercase tracking-tight leading-tight mt-1">
                Fastest Lap Times
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-bold tracking-widest text-white/40">
            <Radio className="w-3.5 h-3.5 text-primary" />
            {liveCount} {liveCount === 1 ? 'RIG ACTIVE' : 'RIGS ACTIVE'}
          </div>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-14 rounded bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : ranked.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Radio className="w-8 h-8 text-white/20 mx-auto mb-3" />
            <p className="text-white/50 text-sm font-medium">No live laps being recorded right now</p>
            <p className="text-white/30 text-xs mt-1">Check back when the rigs are running</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {ranked.map((r, idx) => {
              const isLive = r.is_live || r.status === 'live';
              const podium = idx < 3;
              const medal = ['#facc15', '#cbd5e1', '#f59e0b'][idx];
              return (
                <div
                  key={r.id}
                  className="flex items-center gap-3 md:gap-4 rounded bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 hover:border-primary/30 transition-colors px-3 md:px-4 py-3"
                >
                  <div className="flex items-center justify-center w-7 md:w-8 shrink-0">
                    {podium ? (
                      <Trophy className="w-4 h-4 md:w-5 md:h-5" style={{ color: medal }} />
                    ) : (
                      <span className="text-white/40 font-mono text-sm font-bold">{idx + 1}</span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-display font-bold text-sm md:text-base truncate">
                        {r.driver_name}
                      </span>
                      {isLive && (
                        <span className="inline-flex items-center gap-1 shrink-0">
                          <span className="animate-pulse h-1.5 w-1.5 rounded-full bg-primary" />
                          <span className="text-primary text-[9px] font-black tracking-widest">LIVE</span>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 md:gap-3 mt-0.5 text-white/40 text-[11px] md:text-xs">
                      <span className="flex items-center gap-1 truncate">
                        <span className="text-primary/60 font-semibold">{r.rig_name || r.rig_key}</span>
                      </span>
                      {r.track && (
                        <span className="hidden sm:flex items-center gap-1 truncate">
                          <span className="text-white/20">·</span>
                          {r.track}
                        </span>
                      )}
                      {r.car && (
                        <span className="hidden md:flex items-center gap-1 truncate">
                          <Car className="w-3 h-3" />
                          {r.car}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="font-mono text-white text-base md:text-lg font-bold tabular-nums leading-none">
                      {r.best_lap_time}
                    </div>
                    {r.top_speed_kph != null && (
                      <div className="flex items-center justify-end gap-1 text-white/30 text-[10px] md:text-[11px] mt-1">
                        <Gauge className="w-3 h-3" />
                        {Math.round(r.top_speed_kph)} km/h
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}